import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useMantineColorScheme, Checkbox } from "@mantine/core";
import { useDualTranslation } from "../utils/i18nHelpers";

const getCheckboxState = (key, selectedOptions, tierCount = 1) => {
  key = Number(key);
  // If more than one tier, disable Subscribed Block, Subscribed Block Rate, and Ratchet Demand
  if (tierCount > 1 && (key === 5 || key === 6 || key === 7)) {
    return { disabled: true, mandatory: false };
  }
  // Demand Value (2) is mandatory if Penalty (4) is selected
  if (key === 2) {
    return { disabled: false, mandatory: selectedOptions.includes(4) };
  }
  // Minimum Billing (3) is disabled if Penalty (4) is selected
  if (key === 3) {
    return { disabled: selectedOptions.includes(4), mandatory: false };
  }
  // Penalty (4) always enabled, not mandatory
  if (key === 4) {
    return { disabled: false, mandatory: false };
  }
  if (key === 7) {
    // Enable Ratchet if Minimum Billing (3) is selected, unless Penalty (4) is selected or Demand Value (2) is not selected
    if (
      selectedOptions.includes(3) &&
      !selectedOptions.includes(4) &&
      selectedOptions.includes(2)
    ) {
      return { disabled: false, mandatory: false };
    }
    // If Minimum Billing (3) is selected, always enable Ratchet (even if not selected)
    if (
      selectedOptions.includes(3) &&
      !selectedOptions.includes(4)
    ) {
      return { disabled: false, mandatory: false };
    }
    return {
      disabled: !selectedOptions.includes(2) || selectedOptions.includes(4),
      mandatory: false,
    };
  }
  // Subscribed Block (5) is enabled if Penalty (4), Subscribed Block Rate (6), or itself is selected
  if (key === 5) {
    return {
      disabled: !(
        selectedOptions.includes(4) ||
        selectedOptions.includes(6) ||
        selectedOptions.includes(5)
      ),
      mandatory: false,
    };
  }
  // Subscribed Block Rate (6) is enabled and checked by default if Subscribed Block (5) is selected
  if (key === 6) {
    const enabled = selectedOptions.includes(4) || selectedOptions.includes(5);
    const mandatory = selectedOptions.includes(5);
    return { disabled: !enabled, mandatory };
  }
  // Demand Charge (1), Fixed (8), Variable (9) always enabled
  if (key === 1 || key === 8 || key === 9)
    return { disabled: false, mandatory: false };
  return { disabled: false, mandatory: false };
};

const DemandNoticeSelector = ({
  DEMAND_NOTICE,
  selectedOptions,
  handleOptionClick,
  removeOptionFromAllTiers,
  tierCount = 1,
  isMasterContext,
  isReadOnly,
  onTariffChange
}) => {
  const { colorScheme } = useMantineColorScheme();
  const { t } = useDualTranslation();
  // Generate a robust, collision-resistant id without relying on Math.random
  const snackbarId = useRef((() => {
    const prefix = 'snackbar-custom-Rand-';
    if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
      const arr = new Uint32Array(3);
      window.crypto.getRandomValues(arr);
      return prefix + arr.map(n => n.toString(36)).join('').slice(0, 12);
    }
    const ts = Date.now().toString(36);
    if (!window.__snackbarCounter) window.__snackbarCounter = 0;
    const cnt = (window.__snackbarCounter++).toString(36);
    return prefix + ts + cnt;
  })());
  const [snackbarProps, setSnackbarProps] = useState({
    open: false,
    type: "success",
    canClose: true,
    message: t("demandCharge.snackbar.success"),
    icon: "information_circle",
    button: t("common.close"),
    duration: 5000,
  });

  const callSnackbar = (type, message) => {
    setSnackbarProps({
      type,
      message,
    });
    document.getElementById(snackbarId.current).open = true;
  };
  useEffect(() => {
    // Always ensure Demand Charge (1) is selected
    if (
      !selectedOptions.includes(1) &&
      typeof handleOptionClick === "function"
    ) {
      handleOptionClick(1);
    }
    // List all possible keys
    const allKeys = Object.keys(DEMAND_NOTICE).map(Number);
    // Find all keys that should be disabled (except Demand Value itself)
    const disabledKeys = allKeys.filter(
      (key) =>
        key !== 2 && getCheckboxState(key, selectedOptions, tierCount).disabled
    );
    // Only remove disabled options that are currently selected
    if (typeof removeOptionFromAllTiers === "function") {
      disabledKeys.forEach((key) => {
        if (selectedOptions.includes(key)) {
          removeOptionFromAllTiers(key);
        }
      });
    }
    // Enforce mutual exclusivity for Minimum Billing (3) and Penalty (4)
    if (selectedOptions.includes(3) && selectedOptions.includes(4)) {
      removeOptionFromAllTiers(3);
    }
    // If Penalty (4) is selected, ensure Demand Value (2) is always selected
    if (
      selectedOptions.includes(4) &&
      !selectedOptions.includes(2) &&
      typeof handleOptionClick === "function"
    ) {
      handleOptionClick(2);
    }
    // Disable Ratchet (7) when Penalty (4) is enabled
    if (selectedOptions.includes(4) && selectedOptions.includes(7)) {
      removeOptionFromAllTiers(7);
    }
    // Auto-select Subscribed Block Rate (6) if Subscribed Block (5) is selected and 6 is not
    if (
      selectedOptions.includes(5) &&
      !selectedOptions.includes(6) &&
      typeof handleOptionClick === "function"
    ) {
      handleOptionClick(6);
    }
    // Remove Subscribed Block (5) and Subscribed Block Rate (6) if Penalty (4) is unchecked
    if (
      !selectedOptions.includes(4) &&
      (selectedOptions.includes(5) || selectedOptions.includes(6)) &&
      typeof removeOptionFromAllTiers === "function"
    ) {
      if (selectedOptions.includes(5)) removeOptionFromAllTiers(5);
      if (selectedOptions.includes(6)) removeOptionFromAllTiers(6);
    }
  }, [
    selectedOptions,
    DEMAND_NOTICE,
    removeOptionFromAllTiers,
    tierCount,
    handleOptionClick,
  ]);
  const { type, message } = snackbarProps;

  return (
    <>
      <se-snackbar
        id={snackbarId.current}
        type={type}
        icon="information_stroke"
        message={message}
        duration="3000"
      ></se-snackbar>
      <div className="row mt-3">
        <div className="col-sm-12 col-md-12 col-lg-12 col-xl-12 ">
          <div className="d-flex align-items-center mb-3">
            <label
              htmlFor="form-weekdays"
              className="flex-shrink-0"
              style={{
                fontSize: "12px",
                fontWeight: "700",
                marginTop: "20px",
                paddingRight: "20px",
              }}
            >
              {t("demandCharge.labels.selectDemandParameters")}
            </label>
            <div
              className="d-flex flex-row justify-content-around mt-2"
              id="form-weekdays"
              style={{ maxWidth: "400px",pointerEvents: isMasterContext && !isReadOnly ? "auto" : "none", opacity: isMasterContext && !isReadOnly ? "1" : "0.5" }}
            >
              {Object.entries(DEMAND_NOTICE)
                .filter(([key]) => Number(key) !== 1 && Number(key) !== 6) // Hide Demand Charge (1) and Subscribed Block Rate (6) from selection
                .map(([key, label]) => {
                  const numKey = Number(key);
                  const { disabled, mandatory } = getCheckboxState(
                    key,
                    selectedOptions,
                    tierCount
                  );
                  const isSelected = selectedOptions.includes(numKey);
                  // Custom onClick logic for Demand Value (2), Minimum Billing (3), and Ratchet (7)
                  const handleClick = () => {
                    if (disabled) return;
                    onTariffChange?.();
                    // Prevent unchecking Demand Value when Penalty is checked
                    if (
                      numKey === 2 &&
                      selectedOptions.includes(4) &&
                      isSelected
                    ) {
                      callSnackbar(
                        "error",
                        t("demandCharge.errors.demandValueMandatory")
                      );
                      return;
                    }
                    // Prevent unchecking Minimum Billing if Ratchet is selected
                    if (
                      numKey === 3 &&
                      selectedOptions.includes(7) &&
                      isSelected
                    ) {
                      return;
                    }
                    // If Minimum Billing (3) is being selected, unselect Ratchet (7)
                    if (
                      numKey === 3 &&
                      !isSelected &&
                      selectedOptions.includes(7) &&
                      typeof removeOptionFromAllTiers === "function"
                    ) {
                      removeOptionFromAllTiers(7);
                    }
                    // If Ratchet (7) is being selected, unselect Minimum Billing (3) if it is selected
                    if (
                      numKey === 7 &&
                      !isSelected &&
                      selectedOptions.includes(3) &&
                      typeof removeOptionFromAllTiers === "function"
                    ) {
                      removeOptionFromAllTiers(3);
                    }
                    // Do NOT auto-select Minimum Billing (3) when selecting Ratchet (7)
                    // Synchronize Subscribed Block (5) and Subscribed Block Rate (6)
                    if (numKey === 5) {
                      if (!isSelected && !selectedOptions.includes(6)) {
                        handleOptionClick(6); // select 6 if 5 is being selected
                      } else if (isSelected && selectedOptions.includes(6)) {
                        handleOptionClick(6); // unselect 6 if 5 is being unselected
                      }
                    }
                    if (numKey === 6) {
                      if (!isSelected && !selectedOptions.includes(5)) {
                        handleOptionClick(5); // select 5 if 6 is being selected
                      } else if (isSelected && selectedOptions.includes(5)) {
                        handleOptionClick(5); // unselect 5 if 6 is being unselected
                      }
                    }
                    handleOptionClick(numKey);
                  };
                  return (
                    <div
                      key={key}
                      className={`display-weekdays-dn pointer${
                        disabled ? " disabled" : ""
                      }`}
                      style={{
                        opacity: disabled ? 0.5 : 1,
                        cursor: disabled ? "not-allowed" : "pointer",
                        display: "inline-block",
                        marginRight: 8,
                      }}
                    >
                      <Checkbox
                        checked={isSelected}
                        disabled={disabled}
                        onChange={handleClick}
                        label={
                          <span
                            className="font-10px"
                            style={{
                              textTransform: "uppercase",
                              position: "relative",
                              display: "inline-block",
                            }}
                          >
                            {t(`demandCharge.notice.${key}`)}
                            {(numKey === 8 || numKey === 9) && (
                              <span
                                style={{
                                  position: "absolute",
                                  left: "50%",
                                  top: "100%",
                                  fontSize: "9px",
                                  lineHeight: 1,
                                  color:
                                    colorScheme === "dark"
                                      ? "#9aa0a6"
                                      : "#6b7280",
                                  pointerEvents: "none",
                                  transform: "translateX(-50%)",
                                }}
                              >
                                {t("demandCharge.labels.optional")}
                              </span>
                            )}
                          </span>
                        }
                        styles={{
                          body: {
                            flexDirection: "column-reverse",
                            alignItems: "center",
                          },
                          label: { paddingLeft: "1px", marginBottom: "6px" },
                        }}
                        size="lg"
                        color="green"
                      />
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

DemandNoticeSelector.propTypes = {
  DEMAND_NOTICE: PropTypes.object.isRequired,
  selectedOptions: PropTypes.array.isRequired,
  handleOptionClick: PropTypes.func.isRequired,
  tierCount: PropTypes.number,
};

export default DemandNoticeSelector;
